import React, { useMemo } from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import type { SubtitleSegment } from "../../lib/types";

interface TimedSubtitleStyle {
  theme: string;
  entrance: "static" | "wordHighlight";
  fontSize?: number;
  textStroke?: string;
  backgroundColor?: string;
  paddingBottom?: string;
  maxWidth?: string;
}

interface TimedSubtitleProps {
  /** 字幕段落数组 */
  data: SubtitleSegment[];
  style: TimedSubtitleStyle;
}

/**
 * 带时间戳的字幕组件
 *
 * 接受一段 JSON 字幕数据（{start, end, text}[]），
 * 在对应的时间范围内显示对应的字幕文本。
 *
 * 支持 static（整段显示）和 wordHighlight（逐字高亮）两种模式。
 */
const TimedSubtitle: React.FC<TimedSubtitleProps> = ({ data, style }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // 当前帧（秒）
  const currentTime = frame / fps;

  // 找到当前激活的字幕段落
  const activeSegment = useMemo(() => {
    // 普通匹配：在时间段内
    const seg = data.find((s) => currentTime >= s.start && currentTime < s.end);
    if (seg) return seg;
    // 边界情况：已过最后一段的结束时间，仍显示最后一段
    if (data.length > 0 && currentTime >= data[data.length - 1].end) {
      return data[data.length - 1];
    }
    return null;
  }, [data, currentTime]);

  if (!activeSegment) return null;

  const text = activeSegment.text;
  const fontSize = style.fontSize || 48;

  // ── 静态模式：整段显示 ──
  if (style.entrance === "static") {
    return (
      <AbsoluteFill
        style={{
          justifyContent: "flex-end",
          alignItems: "center",
          paddingBottom: style.paddingBottom || "15%",
        }}
      >
        <div
          style={{
            width: "100%",
            padding: "16px 0",
            backgroundColor: style.backgroundColor || "transparent",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 4,
              maxWidth: style.maxWidth || "90%",
            }}
          >
            <span
              style={{
                fontSize,
                fontWeight: 700,
                fontFamily: "sans-serif",
                color: style.theme,
                WebkitTextStroke: style.textStroke || "none",
              }}
            >
              {text}
            </span>
          </div>
        </div>
      </AbsoluteFill>
    );
  }

  // ── 逐字高亮模式 ──
  const chars = text.split("");
  const wordDuration = 4; // 每字高亮帧数
  const currentWordIndex = Math.min(
    Math.floor(frame / wordDuration),
    chars.length - 1,
  );

  /** 将 #hex 颜色转为 rgba 字符串 */
  function hexToRgba(hex: string, alpha: number): string {
    const c = hex.replace("#", "");
    if (c.length < 6) return `rgba(255,255,255,${alpha})`;
    const r = parseInt(c.slice(0, 2), 16);
    const g = parseInt(c.slice(2, 4), 16);
    const b = parseInt(c.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }

  return (
    <AbsoluteFill
      style={{
        justifyContent: "flex-end",
        alignItems: "center",
        paddingBottom: style.paddingBottom || "20%",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
          maxWidth: style.maxWidth || "90%",
        }}
      >
        {chars.map((char, i) => {
          const isCurrent = i === currentWordIndex;
          const isPast = i < currentWordIndex;

          // 当前字的弹性放大
          let scale = 1;
          if (isCurrent) {
            const localFrame = frame - i * wordDuration;
            const bounce = spring({
              frame: localFrame,
              fps,
              config: { damping: 8, mass: 0.5, stiffness: 200 },
              durationInFrames: wordDuration,
            });
            scale = interpolate(bounce, [0, 1], [1.3, 1.0]);
          }

          const color = isCurrent
            ? style.theme
            : isPast
              ? hexToRgba(style.theme, 0.6)
              : hexToRgba(style.theme, 0.3);

          const textShadow = isCurrent
            ? `0 0 30px ${style.theme}80, 0 0 60px ${style.theme}40`
            : "none";

          return (
            <span
              key={i}
              style={{
                fontSize,
                fontWeight: 700,
                fontFamily: "sans-serif",
                color,
                WebkitTextStroke: style.textStroke || "6px black",
                textShadow,
                transform: `scale(${scale})`,
                display: "inline-block",
                transition: "color 0.15s ease, text-shadow 0.15s ease",
              }}
            >
              {char === " " ? " " : char}
            </span>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

export default TimedSubtitle;
;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
